/**
 * Example conversion-flow data for the Visualizer Sankey.
 *
 * Illustrative only — shown to logged-out users and to authenticated users
 * with no live data yet. Three columns: Channel → Subchannel → Outcome.
 *
 * Numbers reconcile against the canonical Acme demo brand fixture
 * (`lib/fixtures/demo-brand.ts`):
 *   - Channel-level visitors are spend-weighted from DEMO_CHANNELS
 *   - Subchannel-level visitors are spend-weighted from DEMO_SUBCHANNELS
 *   - Outcome counts come from DEMO_TRACKED_PROPERTIES
 *   - Total sessions / purchases / revenue come from the canon helpers
 */

import { CAMPAIGN_CHANNEL_PALETTE, type CampaignChannelKey } from '@/lib/campaigns/fallback-data'
import {
  DEMO_CHANNELS,
  DEMO_SUBCHANNELS,
  DEMO_CUSTOMER_SEGMENTS,
  DEMO_TRACKED_PROPERTIES,
  blendedRoas,
  totalMonthlySpendCents,
  type DemoChannelKey,
} from '@/lib/fixtures/demo-brand'

export type ChannelKey = CampaignChannelKey

/** Channel palette — matches CAMPAIGN_CHANNEL_PALETTE / --color-ch-* tokens. */
export const CHANNEL_COLOR: Record<ChannelKey, string> = {
  search: CAMPAIGN_CHANNEL_PALETTE.search.hex,
  social: CAMPAIGN_CHANNEL_PALETTE.social.hex,
  tv: CAMPAIGN_CHANNEL_PALETTE.tv.hex,
  programmatic: CAMPAIGN_CHANNEL_PALETTE.programmatic.hex,
  audio: CAMPAIGN_CHANNEL_PALETTE.audio.hex,
  retail: CAMPAIGN_CHANNEL_PALETTE.retail.hex,
  other: CAMPAIGN_CHANNEL_PALETTE.other.hex,
}

export const OUTCOME_COLOR = {
  converted: '#0A8A6E',
  browsed: '#94A3B8',
} as const

export type FlowColumn = 0 | 1 | 2

export interface FlowNode {
  id: string
  name: string
  column: FlowColumn
  channel?: ChannelKey
  kind: 'channel' | 'subchannel' | 'outcome'
  color?: string
  sub?: string
}

export interface FlowLink {
  source: string
  target: string
  value: number
  pct: number
  pctLabel: string
}

export interface ConversionFlowTotals {
  sessions: number
  purchases: number
  revenueCents: number
}

export interface ConversionFlowData {
  nodes: FlowNode[]
  links: FlowLink[]
  totals: ConversionFlowTotals
}

// ── Canon-derived totals ───────────────────────────────────────────────────

const visitors = DEMO_CUSTOMER_SEGMENTS.find((segment) => segment.key === 'all-visitors')?.count ?? 48_200
const purchases = DEMO_TRACKED_PROPERTIES.find((property) => property.key === 'purchase')?.events ?? 142
const spendTotal = totalMonthlySpendCents()
const activeChannels = Object.values(DEMO_CHANNELS).filter((channel) => channel.spendCents > 0)
const activeSubchannels = DEMO_SUBCHANNELS.filter((sub) => sub.spendCents > 0)

// Conversion rate per channel — modulate the global conversion rate by the
// channel's ROAS (Search converts more than Audio for the same visitor count).
const baseConvRate = visitors > 0 ? purchases / visitors : 0
const channelRoasAvg =
  activeChannels.reduce((sum, c) => sum + c.roas, 0) / Math.max(activeChannels.length, 1)

function channelVisitors(channel: { spendCents: number }): number {
  if (spendTotal <= 0) return 0
  return Math.round((channel.spendCents / spendTotal) * visitors)
}

function subchannelVisitors(sub: { spendCents: number; channel: DemoChannelKey }): number {
  const parent = DEMO_CHANNELS[sub.channel]
  if (!parent || parent.spendCents <= 0) return 0
  const channelTotal = channelVisitors(parent)
  return Math.round((sub.spendCents / parent.spendCents) * channelTotal)
}

function subchannelPurchases(sub: { roas: number; spendCents: number; channel: DemoChannelKey }): number {
  const subVisits = subchannelVisitors(sub)
  if (subVisits <= 0) return 0
  // Higher-ROAS subchannels convert more visitors.
  const roasFactor = channelRoasAvg > 0 ? sub.roas / channelRoasAvg : 1
  return Math.max(0, Math.round(subVisits * baseConvRate * roasFactor))
}

function pct(value: number, total: number): number {
  if (total <= 0) return 0
  return Math.round((value / total) * 100)
}

// Build subchannel purchase rollup so the total reconciles to `purchases`.
// We compute raw subchannel purchases then proportionally normalize them so
// the sum across all subchannels equals the canon total.
const rawSubPurchases = activeSubchannels.map((sub) => ({
  sub,
  raw: subchannelPurchases(sub),
}))
const rawSubPurchaseSum = rawSubPurchases.reduce((s, r) => s + r.raw, 0)
const normFactor = rawSubPurchaseSum > 0 ? purchases / rawSubPurchaseSum : 0
const subPurchaseById = new Map(
  rawSubPurchases.map((r) => [r.sub.key, Math.max(0, Math.round(r.raw * normFactor))]),
)
const subBrowseById = new Map(
  rawSubPurchases.map((r) => [r.sub.key, Math.max(0, subchannelVisitors(r.sub) - (subPurchaseById.get(r.sub.key) ?? 0))]),
)

// Outcome totals — purchases reconcile to canon; browsed = visitors − purchases.
const browsed = Math.max(0, visitors - purchases)

// ── EXAMPLE_FLOW ───────────────────────────────────────────────────────────

export const EXAMPLE_FLOW: ConversionFlowData = {
  nodes: [
    // Col 0 — channels
    ...activeChannels.map((channel) => ({
      id: channel.key,
      name: channel.label,
      column: 0 as const,
      channel: channel.key as DemoChannelKey,
      kind: 'channel' as const,
      sub: `$${Math.round(channel.spendCents / 100_000)}k spend · ${channel.roas.toFixed(1)}× ROAS`,
    })),
    // Col 1 — subchannels
    ...activeSubchannels.map((sub) => ({
      id: `sub-${sub.key}`,
      name: sub.name,
      column: 1 as const,
      channel: sub.channel as DemoChannelKey,
      kind: 'subchannel' as const,
      sub: `${subchannelVisitors(sub).toLocaleString()} sessions`,
    })),
    // Col 2 — outcomes
    {
      id: 'outcome-purchased',
      name: 'Purchased',
      column: 2,
      kind: 'outcome',
      color: OUTCOME_COLOR.converted,
      sub: `${purchases.toLocaleString()} conversions`,
    },
    {
      id: 'outcome-browsed',
      name: 'Browsed only',
      column: 2,
      kind: 'outcome',
      color: OUTCOME_COLOR.browsed,
      sub: `${browsed.toLocaleString()} sessions`,
    },
  ],
  links: [
    // Channel → Subchannel (one link per active subchannel)
    ...activeSubchannels.map((sub) => {
      const subVisits = subchannelVisitors(sub)
      const parent = DEMO_CHANNELS[sub.channel]
      const parentVisits = channelVisitors(parent)
      return {
        source: sub.channel,
        target: `sub-${sub.key}`,
        value: subVisits,
        pct: pct(subVisits, parentVisits),
        pctLabel: 'of channel',
      }
    }),
    // Subchannel → Outcome (purchased + browsed for each subchannel)
    ...activeSubchannels.flatMap((sub) => {
      const subVisits = subchannelVisitors(sub)
      const subPurch = subPurchaseById.get(sub.key) ?? 0
      const subBrowsed = subBrowseById.get(sub.key) ?? 0
      return [
        {
          source: `sub-${sub.key}`,
          target: 'outcome-purchased',
          value: subPurch,
          pct: pct(subPurch, subVisits),
          pctLabel: 'conv. rate',
        },
        {
          source: `sub-${sub.key}`,
          target: 'outcome-browsed',
          value: subBrowsed,
          pct: pct(subBrowsed, subVisits),
          pctLabel: 'browsed',
        },
      ]
    }),
  ],
  totals: {
    sessions: visitors,
    purchases,
    revenueCents: Math.round(totalMonthlySpendCents() * blendedRoas()),
  },
}

/** @deprecated Use EXAMPLE_FLOW.totals */
export const FLOW_TOTALS = {
  sessions: EXAMPLE_FLOW.totals.sessions,
  conversions: EXAMPLE_FLOW.totals.purchases,
  get conversionRate() {
    return (this.conversions / this.sessions) * 100
  },
}

/** @deprecated Use EXAMPLE_FLOW.nodes */
export const FLOW_NODES = EXAMPLE_FLOW.nodes

/** @deprecated Use EXAMPLE_FLOW.links */
export const FLOW_LINKS = EXAMPLE_FLOW.links

export function colorForNode(node: FlowNode): string {
  if (node.color) return node.color
  if (node.channel) return CHANNEL_COLOR[node.channel]
  return '#94A3B8'
}

export function colorForLink(link: FlowLink, nodeById: Map<string, FlowNode>): string {
  const src = nodeById.get(link.source)
  return src ? colorForNode(src) : '#94A3B8'
}
